import { gql } from '@apollo/client';

/**
 * Batch update (card 20): NationBuilder's "Batch update", on a selection of the
 * contact list or on everyone on a list. Preview, run, undo, and the history.
 *
 * Requests and real responses: ilbp-backend CRM_CONTACT_QUERY_FRONTEND.md §23.
 * Every one is admin-only. Running and undoing share the `crm-bulk` budget
 * (10 a minute) with the bulk tag and list changes.
 */

/** Admins (point people), membership plans and the stages in use: the dialog's choices, once a visit. */
export const CRM_BATCH_CHOICES = gql`
  query CrmBatchChoices {
    crmAdmins {
      id
      firstName
      lastName
    }
    crmMembershipPlans {
      id
      name
      total
      active
    }
    stages: crmContactFacet(field: LIFECYCLE_STAGE, take: 50) {
      value
      count
    }
  }
`;

/** What the change would do to the selection now: the count to confirm, per part, and warnings. */
export const CRM_BATCH_PREVIEW = gql`
  query CrmBatchUpdatePreview($filter: CrmContactFilter!, $change: CrmBatchUpdateInput!, $segmentId: Int) {
    crmBatchUpdatePreview(filter: $filter, change: $change, segmentId: $segmentId) {
      matched
      willChange
      skipped
      parts {
        key
        label
        changed
        unchanged
      }
      warnings
    }
  }
`;

const BATCH_FIELDS = `
  id
  kind
  status
  processed
  undoProcessed
  error
  finishedAt
  canResume
  summary
  matched
  changed
  skipped
  parts {
    key
    label
    changed
    unchanged
  }
  note
  consentGivenAt
  segmentId
  createdAt
  undoneAt
  undoRestored
  undoKept
  canUndo
  undoNote
  createdBy {
    id
    firstName
    lastName
  }
  undoneBy {
    id
    firstName
    lastName
  }
  segment {
    id
    name
    type
  }
`;

/** Runs it, on the count the admin confirmed. On any other count the API changes nothing. */
export const CRM_RUN_BATCH_UPDATE = gql`
  mutation CrmRunBatchUpdate($filter: CrmContactFilter!, $expectedCount: Int!, $change: CrmBatchUpdateInput!, $segmentId: Int) {
    crmRunBatchUpdate(filter: $filter, expectedCount: $expectedCount, change: $change, segmentId: $segmentId) {
      ${BATCH_FIELDS}
    }
  }
`;

export const CRM_UNDO_BATCH_UPDATE = gql`
  mutation CrmUndoBatchUpdate($id: Int!) {
    crmUndoBatchUpdate(id: $id) {
      ${BATCH_FIELDS}
    }
  }
`;

/** The history, newest first: every batch, or one list's. */
export const CRM_BATCH_UPDATES = gql`
  query CrmBatchUpdates($filter: CrmBatchUpdateFilter, $take: Int, $after: String) {
    crmBatchUpdates(filter: $filter, take: $take, after: $after) {
      total
      hasNextPage
      endCursor
      items {
        ${BATCH_FIELDS}
      }
    }
  }
`;

/** One batch, for its progress while it runs on in the background. */
export const CRM_BATCH_UPDATE = gql`
  query CrmBatchUpdate($id: Int!) {
    crmBatchUpdate(id: $id) {
      ${BATCH_FIELDS}
    }
  }
`;

/** Picks up a batch that stopped part way: its change, or its undo. */
export const CRM_RESUME_BATCH_UPDATE = gql`
  mutation CrmResumeBatchUpdate($id: Int!) {
    crmResumeBatchUpdate(id: $id) {
      ${BATCH_FIELDS}
    }
  }
`;
